import { QueryClient } from '@tanstack/react-query';

// Configure React Query with optimal settings for coach dashboard
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Keep data in memory for 10 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)

      // Don't refetch when window regains focus (better UX for dashboard)
      refetchOnWindowFocus: false,

      // Always refetch when reconnecting to network
      refetchOnReconnect: 'always',

      // Retry failed requests up to 3 times
      retry: (failureCount, error: any) => {
        // Don't retry 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) return false;
        // Don't retry 401 (unauthorized) - user needs to re-authenticate
        if (error?.status === 401) return false;
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },

      // Don't retry on mount if data is fresh
      refetchOnMount: false,
    },
    mutations: {
      // Retry failed mutations up to 2 times
      retry: (failureCount, error: any) => {
        if (error?.status >= 400 && error?.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

// Prefetch critical data for better UX
export const prefetchDashboardData = async (sessionId: string) => {
  // Prefetch session metadata (rarely changes)
  await queryClient.prefetchQuery({
    queryKey: ['session', 'metadata', sessionId],
    queryFn: () => Promise.resolve({ id: sessionId }), // Placeholder - will be replaced
    staleTime: Infinity, // Metadata doesn't change
  });

  // Prefetch basic session info
  await queryClient.prefetchQuery({
    queryKey: ['session', 'info', sessionId],
    queryFn: () => Promise.resolve({ id: sessionId }), // Placeholder - will be replaced
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Cache invalidation helpers
export const invalidateSessionData = (sessionId: string) => {
  queryClient.invalidateQueries({
    queryKey: ['session', sessionId],
  });
};

export const invalidateAllSessionData = () => {
  queryClient.invalidateQueries({
    queryKey: ['session'],
  });
};

export const clearAllCaches = () => {
  queryClient.clear();
};
